import React, { useCallback, useMemo } from 'react';

import { useRenderingContext } from 'src/modules/renderings/hooks';
import { FeatureTracking } from 'src/shared/constants/FeatureFlag';
import {
  HiDefRenderingVersionState,
  RenderingVersion,
  useCreateHiDefRenderingVersionMutation,
} from 'src/shared/graphql/generated';
import {
  TranslationNamespace,
  useReactNativeWebView,
  useTranslate,
} from 'src/shared/hooks';
import { MobileAppActionEnum } from 'src/shared/hooks/useReactNativeWebView/types';
import { Page, buttonPressed } from 'src/shared/telemetry';

import { BaseCTAButton } from './BaseCTAButton';

export const REQUEST_REVISION_TEST_ID = 'request-revision';

type RequestRevisionProps = {
  primaryCallToAction?: boolean;
};
const RequestRevision = ({
  primaryCallToAction = true,
}: RequestRevisionProps) => {
  const t = useTranslate(TranslationNamespace.SHARED);
  const { renderings, trackingData } = useRenderingContext();
  const rendering = renderings?.[0];
  const [createVersion, { loading }] = useCreateHiDefRenderingVersionMutation();
  const [isWebview, sendMessageToWebView] = useReactNativeWebView();

  const requestRevisionButtonText = t('renderings.requestRevision');

  const buttonPressedTrackingData = useMemo(
    () => ({
      ...trackingData,
      button_text: requestRevisionButtonText,
      feature: FeatureTracking.RENDERING,
      track_id: 'revision-cta',
      page_or_screen_name: Page.RENDERINGS_HUB,
      primary_cta: false,
    }),
    [trackingData, requestRevisionButtonText],
  );

  const unstartedRevision = rendering?.versions.find(
    version =>
      version.state === HiDefRenderingVersionState.NotStarted &&
      version.versionType === RenderingVersion.Revision,
  );

  const isDisabled =
    !rendering?.canCreateRevision && unstartedRevision === undefined;

  const openLinkExternally = useCallback(
    async (variables: {
      hiDefRenderingId: string;
      versionType: RenderingVersion;
    }) => {
      const { data } = await createVersion({ variables });
      const requestFormUrl = data?.createHiDefRenderingVersion.requestFormUrl;

      if (requestFormUrl) {
        sendMessageToWebView(
          JSON.stringify({
            action: MobileAppActionEnum.OPEN_LINK,
            data: requestFormUrl,
          }),
        );
      }
    },
    [createVersion, sendMessageToWebView],
  );

  const handleRequestRevision = useCallback(async () => {
    if (!rendering) {
      return;
    }

    const variables = {
      hiDefRenderingId: rendering.id.toString(),
      versionType: RenderingVersion.Revision,
    };

    buttonPressed(buttonPressedTrackingData);

    if (isWebview) {
      await openLinkExternally(variables);
      return;
    }

    // Open blank window immediately to ensure popup is not blocked due
    // to delay between user input and call to window.open
    const formWindow = window.open('', 'hi_def_rendering_form');

    if (formWindow) {
      formWindow.document.write('Loading form...');
    }

    const { data } = await createVersion({ variables });
    const requestFormUrl = data?.createHiDefRenderingVersion.requestFormUrl;
    if (!!formWindow && requestFormUrl) {
      formWindow.location.href = requestFormUrl;
    } else {
      formWindow?.close();
    }
  }, [
    rendering,
    buttonPressedTrackingData,
    isWebview,
    createVersion,
    openLinkExternally,
  ]);

  if (rendering === undefined) {
    return null;
  }

  return (
    <BaseCTAButton
      fill={primaryCallToAction ? 'solid' : 'outline'}
      isDisabled={isDisabled}
      isLoading={loading}
      onClick={handleRequestRevision}
      primaryCallToAction={primaryCallToAction}
      testId={REQUEST_REVISION_TEST_ID}
    >
      {requestRevisionButtonText}
    </BaseCTAButton>
  );
};

export { RequestRevision };
